import React, { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Plus, Trash2, X } from 'lucide-react';
import { apiService } from '../connectionsApiService';
import { createDataStore, testDataStore } from '../dataStoreApi';

interface Props {
    connectorKey: string;
    onClose: () => void;
    onSubmit: (data: any) => void;
    initialData?: any;
}

type AuthType = 'none' | 'bearer' | 'apiKey' | 'basic';
type ApiKeyLocation = 'header' | 'query';

interface HeaderRow {
    id: number;
    name: string;
    value: string;
    secret: boolean;
}

const LOG_PREFIX = '[RestApiConnection]';
const SECRET_PLACEHOLDER = '********';
const ENVIRONMENTS = ['Development', 'Staging', 'Production', 'Other'];
const HDR_PREFIX = 'hdr:';

function logRest(level: 'app' | 'warning' | 'error', event: string, extra?: Record<string, unknown>): void {
    const payload = extra ? { event, ...extra } : { event };
    if (level === 'error') {
        console.error(LOG_PREFIX, '[error]', payload);
        return;
    }
    if (level === 'warning') {
        console.warn(LOG_PREFIX, '[warning]', payload);
        return;
    }
    console.info(LOG_PREFIX, '[app]', payload);
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function readConfig(initialData?: any): Record<string, unknown> {
    const config = asRecord(initialData?.config);
    const params = asRecord(config.params);
    return Object.keys(params).length ? params : config;
}

function nextHeaderId(rows: HeaderRow[]): number {
    return rows.reduce((max, row) => Math.max(max, row.id), 0) + 1;
}

function loadHeaders(config: Record<string, unknown>, auth: Record<string, unknown>): HeaderRow[] {
    const rows: HeaderRow[] = [];
    const publicHeaders = asRecord(config.headers);
    Object.entries(publicHeaders).forEach(([name, value], index) => {
        if (!name) return;
        rows.push({ id: index + 1, name, value: String(value ?? ''), secret: false });
    });
    Object.keys(auth).forEach((key) => {
        if (!key.toLowerCase().startsWith(HDR_PREFIX)) return;
        rows.push({
            id: nextHeaderId(rows),
            name: key.slice(HDR_PREFIX.length),
            value: '',
            secret: true,
        });
    });
    return rows;
}

function resolveAuthType(raw: unknown): AuthType {
    const value = String(raw || 'none').toLowerCase().replace(/[-_]/g, '');
    if (value === 'bearer' || value === 'token') return 'bearer';
    if (value === 'apikey') return 'apiKey';
    if (value === 'basic' || value === 'userpass') return 'basic';
    return 'none';
}

const RestApiConnectionForm: React.FC<Props> = ({ connectorKey, onClose, onSubmit, initialData }) => {
    const existingId = initialData?.id ? String(initialData.id) : '';
    const isEditing = Boolean(existingId);
    const initialConfig = useMemo(() => readConfig(initialData), [initialData]);
    const initialAuth = useMemo(() => asRecord(initialData?.auth), [initialData]);

    const [name, setName] = useState(String(initialData?.name || ''));
    const [environment, setEnvironment] = useState(String(initialData?.environment || 'Development'));
    const [baseUrl, setBaseUrl] = useState(String(initialConfig.baseUrl || ''));
    const [healthPath, setHealthPath] = useState(String(initialConfig.healthPath || ''));
    const [authType, setAuthType] = useState<AuthType>(resolveAuthType(initialAuth.type));
    const [token, setToken] = useState('');
    const [apiKeyName, setApiKeyName] = useState(String(initialAuth.apiKeyName || 'X-API-Key'));
    const [apiKeyValue, setApiKeyValue] = useState('');
    const [apiKeyLocation, setApiKeyLocation] = useState<ApiKeyLocation>(
        String(initialAuth.apiKeyLocation || 'header').toLowerCase() === 'query' ? 'query' : 'header'
    );
    const [username, setUsername] = useState(String(initialAuth.username || ''));
    const [password, setPassword] = useState('');
    const [headers, setHeaders] = useState<HeaderRow[]>(() => loadHeaders(initialConfig, initialAuth));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isTesting, setIsTesting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

    const keptSecrets = useMemo(() => ({
        token: Boolean(initialAuth.token),
        apiKeyValue: Boolean(initialAuth.apiKeyValue || initialAuth.apiKey),
        password: Boolean(initialAuth.password),
        headers: new Set(
            Object.keys(initialAuth)
                .filter((key) => key.toLowerCase().startsWith(HDR_PREFIX))
                .map((key) => key.slice(HDR_PREFIX.length).toLowerCase())
        ),
    }), [initialAuth]);

    const updateHeader = (id: number, patch: Partial<HeaderRow>) => {
        setHeaders((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    };

    const validate = (): boolean => {
        const next: Record<string, string> = {};
        if (!name.trim()) next.name = 'Required field';
        if (!baseUrl.trim()) next.baseUrl = 'Required field';
        else if (!/^https?:\/\//i.test(baseUrl.trim())) next.baseUrl = 'Use an http or https URL';
        if (authType === 'bearer' && !token.trim() && !(isEditing && keptSecrets.token)) {
            next.token = 'Required field';
        }
        if (authType === 'apiKey') {
            if (!apiKeyName.trim()) next.apiKeyName = 'Required field';
            if (!apiKeyValue.trim() && !(isEditing && keptSecrets.apiKeyValue)) next.apiKeyValue = 'Required field';
        }
        if (authType === 'basic') {
            if (!username.trim()) next.username = 'Required field';
            if (!password.trim() && !(isEditing && keptSecrets.password)) next.password = 'Required field';
        }
        headers.forEach((row) => {
            if (row.value && !row.name.trim()) next[`header-${row.id}`] = 'Header name is required';
        });
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const buildPayload = (forSave: boolean) => {
        const publicHeaders: Record<string, string> = {};
        const auth: Record<string, string> = { type: authType };

        headers.forEach((row) => {
            const headerName = row.name.trim();
            if (!headerName) return;
            if (row.secret) {
                const value = row.value.trim();
                if (value) auth[`${HDR_PREFIX}${headerName}`] = value;
                else if (forSave && isEditing && keptSecrets.headers.has(headerName.toLowerCase())) {
                    auth[`${HDR_PREFIX}${headerName}`] = SECRET_PLACEHOLDER;
                }
                return;
            }
            publicHeaders[headerName] = row.value;
        });

        if (authType === 'bearer') {
            if (token.trim()) auth.token = token.trim();
            else if (forSave && isEditing && keptSecrets.token) auth.token = SECRET_PLACEHOLDER;
        }
        if (authType === 'apiKey') {
            auth.apiKeyName = apiKeyName.trim();
            auth.apiKeyLocation = apiKeyLocation;
            if (apiKeyValue.trim()) auth.apiKeyValue = apiKeyValue.trim();
            else if (forSave && isEditing && keptSecrets.apiKeyValue) auth.apiKeyValue = SECRET_PLACEHOLDER;
        }
        if (authType === 'basic') {
            auth.username = username.trim();
            if (password.trim()) auth.password = password;
            else if (forSave && isEditing && keptSecrets.password) auth.password = SECRET_PLACEHOLDER;
        }

        return {
            connectorKey: 'api_rest',
            name: name.trim(),
            environment,
            config: {
                baseUrl: baseUrl.trim(),
                ...(healthPath.trim() ? { healthPath: healthPath.trim() } : {}),
                ...(Object.keys(publicHeaders).length ? { headers: publicHeaders } : {}),
            },
            auth,
        };
    };

    const handleTest = async () => {
        if (!validate()) return;
        setIsTesting(true);
        setTestResult(null);
        logRest('app', 'test_start', { connectorKey });
        try {
            const result = await testDataStore(buildPayload(false));
            setTestResult(result);
            logRest(result.success ? 'app' : 'warning', 'test_done', { success: result.success });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Error connecting to the API server.';
            logRest('error', 'test_failed', { message });
            setTestResult({ success: false, message });
        } finally {
            setIsTesting(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isSaving || !validate()) return;
        setIsSaving(true);
        setTestResult(null);
        logRest('app', existingId ? 'update_start' : 'create_start', { connectorKey, id: existingId || undefined });
        try {
            const payload = buildPayload(true);
            const data = existingId
                ? await apiService.put(`/DataStores/${existingId}`, payload, 'backend')
                : await createDataStore(payload);
            logRest('app', existingId ? 'update_ok' : 'create_ok', { id: data?.id });
            onSubmit(data);
            onClose();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Error creating connection';
            logRest('error', existingId ? 'update_failed' : 'create_failed', { message });
            setTestResult({ success: false, message });
        } finally {
            setIsSaving(false);
        }
    };

    const fieldClass = (key: string) =>
        `bg-neutral-800 border ${errors[key] ? 'border-red-500' : 'border-neutral-700'} rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 transition-colors`;

    return (
        <div className="portal-content fixed inset-0 z-[110] bg-black/95 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between p-5 sm:p-6 border-b border-neutral-800 shrink-0">
                    <div className="flex-1 pr-4">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <h2 className="text-lg sm:text-xl font-bold text-white leading-tight">
                                {isEditing ? 'Edit' : 'REST API'} Connection
                            </h2>
                            <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-400 text-[10px] font-bold uppercase tracking-wider rounded border border-indigo-500/20">
                                APIs
                            </span>
                        </div>
                        <p className="text-gray-400 text-[11px] sm:text-xs mt-1">
                            One REST API: base URL, auth, and any extra headers.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-neutral-800 rounded-lg text-gray-400 hover:text-white transition-colors shrink-0">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 sm:p-10 custom-scrollbar">
                    <form id="rest-api-form" onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-10">
                        <section className="space-y-4">
                            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em]">Connection</h3>
                            <label className="flex flex-col gap-1.5 text-left">
                                <span className="text-xs font-semibold text-gray-400">Name</span>
                                <input value={name} onChange={(e) => setName(e.target.value)} className={fieldClass('name')} placeholder="Billing API" />
                                {errors.name && <span className="text-[10px] text-red-500">{errors.name}</span>}
                            </label>
                            <label className="flex flex-col gap-1.5 text-left">
                                <span className="text-xs font-semibold text-gray-400">Environment</span>
                                <select value={environment} onChange={(e) => setEnvironment(e.target.value)} className={fieldClass('environment')}>
                                    {ENVIRONMENTS.map((item) => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                            </label>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em]">API</h3>
                            <label className="flex flex-col gap-1.5 text-left">
                                <span className="text-xs font-semibold text-gray-400">Base URL</span>
                                <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} className={fieldClass('baseUrl')} placeholder="https://api.example.com" />
                                {errors.baseUrl && <span className="text-[10px] text-red-500">{errors.baseUrl}</span>}
                            </label>
                            <label className="flex flex-col gap-1.5 text-left">
                                <span className="text-xs font-semibold text-gray-400 flex items-center justify-between">
                                    Health path
                                    <span className="text-[10px] font-medium text-gray-600 italic bg-neutral-800/50 px-1.5 py-0.5 rounded">Optional</span>
                                </span>
                                <input value={healthPath} onChange={(e) => setHealthPath(e.target.value)} className={fieldClass('healthPath')} placeholder="/health" />
                            </label>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em]">Authentication</h3>
                            <label className="flex flex-col gap-1.5 text-left">
                                <span className="text-xs font-semibold text-gray-400">Auth type</span>
                                <select value={authType} onChange={(e) => setAuthType(e.target.value as AuthType)} className={fieldClass('authType')}>
                                    <option value="none">None</option>
                                    <option value="bearer">Bearer token</option>
                                    <option value="apiKey">API key</option>
                                    <option value="basic">Basic</option>
                                </select>
                            </label>
                            {authType === 'bearer' && (
                                <label className="flex flex-col gap-1.5 text-left">
                                    <span className="text-xs font-semibold text-gray-400">Bearer token</span>
                                    <input type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)} className={fieldClass('token')} placeholder={isEditing ? 'Leave blank to keep current' : 'Token'} />
                                    {errors.token && <span className="text-[10px] text-red-500">{errors.token}</span>}
                                </label>
                            )}
                            {authType === 'apiKey' && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <label className="flex flex-col gap-1.5 text-left">
                                        <span className="text-xs font-semibold text-gray-400">Key name</span>
                                        <input value={apiKeyName} onChange={(e) => setApiKeyName(e.target.value)} className={fieldClass('apiKeyName')} placeholder="X-API-Key" />
                                        {errors.apiKeyName && <span className="text-[10px] text-red-500">{errors.apiKeyName}</span>}
                                    </label>
                                    <label className="flex flex-col gap-1.5 text-left">
                                        <span className="text-xs font-semibold text-gray-400">Location</span>
                                        <select value={apiKeyLocation} onChange={(e) => setApiKeyLocation(e.target.value as ApiKeyLocation)} className={fieldClass('apiKeyLocation')}>
                                            <option value="header">Header</option>
                                            <option value="query">Query</option>
                                        </select>
                                    </label>
                                    <label className="flex flex-col gap-1.5 text-left sm:col-span-2">
                                        <span className="text-xs font-semibold text-gray-400">API key</span>
                                        <input type="password" autoComplete="new-password" value={apiKeyValue} onChange={(e) => setApiKeyValue(e.target.value)} className={fieldClass('apiKeyValue')} placeholder={isEditing ? 'Leave blank to keep current' : 'Key'} />
                                        {errors.apiKeyValue && <span className="text-[10px] text-red-500">{errors.apiKeyValue}</span>}
                                    </label>
                                </div>
                            )}
                            {authType === 'basic' && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <label className="flex flex-col gap-1.5 text-left">
                                        <span className="text-xs font-semibold text-gray-400">Username</span>
                                        <input value={username} onChange={(e) => setUsername(e.target.value)} className={fieldClass('username')} />
                                        {errors.username && <span className="text-[10px] text-red-500">{errors.username}</span>}
                                    </label>
                                    <label className="flex flex-col gap-1.5 text-left">
                                        <span className="text-xs font-semibold text-gray-400">Password</span>
                                        <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={fieldClass('password')} placeholder={isEditing ? 'Leave blank to keep current' : 'Password'} />
                                        {errors.password && <span className="text-[10px] text-red-500">{errors.password}</span>}
                                    </label>
                                </div>
                            )}
                        </section>

                        <section className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em]">Headers</h3>
                                <button
                                    type="button"
                                    onClick={() => setHeaders((prev) => [...prev, { id: nextHeaderId(prev), name: '', value: '', secret: false }])}
                                    className="text-[10px] flex items-center gap-1 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 px-2 py-1 rounded transition-colors"
                                >
                                    <Plus size={10} /> Add header
                                </button>
                            </div>
                            {headers.length === 0 && (
                                <div className="text-center py-4 border border-dashed border-neutral-800 rounded-lg text-xs text-gray-600">
                                    No extra headers. Auth headers are added from the type above.
                                </div>
                            )}
                            {headers.map((row) => (
                                <div key={row.id} className="flex gap-2 items-start bg-neutral-800/50 p-2 rounded-lg border border-neutral-800">
                                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <input
                                            value={row.name}
                                            onChange={(e) => updateHeader(row.id, { name: e.target.value })}
                                            className="w-full bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-indigo-500"
                                            placeholder="Header name"
                                        />
                                        <input
                                            type={row.secret ? 'password' : 'text'}
                                            autoComplete="new-password"
                                            value={row.value}
                                            onChange={(e) => updateHeader(row.id, { value: e.target.value })}
                                            className="w-full bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-indigo-500"
                                            placeholder={row.secret && isEditing ? 'Leave blank to keep current' : 'Value'}
                                        />
                                        {errors[`header-${row.id}`] && (
                                            <span className="text-[10px] text-red-500 sm:col-span-2">{errors[`header-${row.id}`]}</span>
                                        )}
                                    </div>
                                    <label className="flex items-center gap-1 text-[10px] text-gray-500 pt-1 shrink-0">
                                        <input
                                            type="checkbox"
                                            checked={row.secret}
                                            onChange={(e) => updateHeader(row.id, { secret: e.target.checked })}
                                            className="w-3 h-3 bg-neutral-800 border-neutral-700 rounded text-indigo-600"
                                        />
                                        Secret
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setHeaders((prev) => prev.filter((item) => item.id !== row.id))}
                                        className="p-1 text-neutral-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors mt-0.5"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </section>
                    </form>
                </div>

                {testResult && (
                    <div className={`mx-6 mb-2 px-4 py-3 rounded-lg flex items-start gap-3 ${testResult.success ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`}>
                        {testResult.success ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
                        <p className="text-xs">{testResult.message}</p>
                    </div>
                )}

                <div className="p-5 sm:p-6 bg-neutral-950/50 border-t border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0 rounded-b-2xl">
                    <button
                        type="button"
                        onClick={handleTest}
                        disabled={isTesting}
                        className="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-gray-300 hover:text-white flex items-center justify-center gap-2 disabled:opacity-50 border border-neutral-800 sm:border-none rounded-lg transition-colors"
                    >
                        {isTesting && <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin" />}
                        Test Connection
                    </button>
                    <div className="flex flex-col-reverse sm:flex-row w-full sm:w-auto gap-3">
                        <button onClick={onClose} className="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-gray-400 hover:text-white transition-colors">
                            Cancel
                        </button>
                        <button
                            form="rest-api-form"
                            type="submit"
                            disabled={!testResult?.success || isSaving}
                            className={`w-full sm:w-auto px-8 py-2.5 rounded-xl text-sm font-bold shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${!testResult?.success ? 'bg-neutral-800 text-gray-500 cursor-not-allowed shadow-none' : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-500/20'}`}
                        >
                            {isSaving && <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin" />}
                            {isEditing ? 'Save Changes' : 'Create Connection'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RestApiConnectionForm;
